import {useField, useFormikContext} from 'formik';
import React, {CSSProperties, useCallback, useEffect, useRef} from 'react';
import {FormControl, InputGroup} from 'react-bootstrap';
import styled, {css} from 'styled-components';
import {
    FieldData,
    Dimensions,
} from 'components/customer/JobDashboard/helpers/useProductDimension';
import Excel from 'shared/Excel';

interface DimensionInputProps {
    id: number;
    fieldData: FieldData;
    style?: CSSProperties;
    boxed?: boolean;
}

export const DimensionInput = ({
    id,
    style,
    fieldData,
    boxed = false,
}: DimensionInputProps) => {
    const {name, label} = fieldData;

    const [field, {error, value}] = useField<number>(name);
    const {values, setFieldValue} = useFormikContext<Dimensions>();

    const previousValue = useRef<number>(value);

    const focusHandler = useCallback((event: React.MouseEvent) => {
        event.stopPropagation();
    }, []);

    useEffect(() => {
        if (previousValue.current != value) {
            previousValue.current = value;
            if (fieldData && fieldData.autoUpdateValue) {
                const scope = {
                    ...values,
                    [name]: value,
                };

                const updatedValue = Excel.calculate<number>(
                    fieldData.autoUpdateValue.value,
                    scope
                );

                if (updatedValue) {
                    void setFieldValue(
                        fieldData.autoUpdateValue.field,
                        updatedValue
                    );
                }
            }
        }
    }, [value, fieldData]);

    return (
        <DimensionContainer onClick={focusHandler}>
            <DimensionGroup
                $hasError={typeof error !== 'undefined'}
                $boxed={boxed}
                style={style}>
                <label htmlFor={`${id}-${name}`}>{label}</label>
                <FormControl
                    data-no-drag
                    id={`${id}-${name}`}
                    type="number"
                    {...field}
                />
                <label htmlFor={`${id}-${name}`}>mm</label>
            </DimensionGroup>
            {error ? <Error $inline={boxed}>{error}</Error> : null}
        </DimensionContainer>
    );
};

const DimensionContainer = styled.div`
    position: relative;
`;

const Error = styled.div<{$inline?: boolean}>`
    position: absolute;
    z-index: 1;
    color: white;
    background: ${({theme}) => theme.colors.errorLabel?.main};
    top: 2px;
    left: calc(100% + 15px);
    width: 240px;
    border-radius: 4px;
    padding: 2px;

    &::before {
        content: '';
        width: 0;
        height: 0;
        border-style: solid;
        border-top: 5px solid transparent;
        border-bottom: 6px solid transparent;
        border-right: 10px solid ${({theme}) => theme.colors.errorLabel?.main};
        border-left: 0;
        position: absolute;
        right: 99%;
        top: 5px;
    }

    ${({$inline}) =>
        $inline &&
        `
        position: static;
        width: auto;
        margin-top: 6px;
        padding: 2px 6px;

        &::before {
            display: none;
        }
    `}
`;

const DimensionGroup = styled(InputGroup)<{
    $hasError?: boolean;
    $boxed?: boolean;
}>`
    align-items: center;
    border: 1px solid
        ${({theme, $hasError}) =>
            $hasError
                ? theme.colors.errorLabel?.main
                : theme.colors.primary?.main};
    box-shadow: 2px 2px
        ${({theme, $hasError}) =>
            $hasError
                ? theme.colors.errorLabel?.main
                : theme.colors.primary?.main};
    border-radius: 8px;
    height: 28px;
    padding: 0 5px;
    background: white;
    color: ${({theme, $hasError}) =>
        $hasError ? theme.colors.errorLabel?.main : 'black'};

    label {
        margin: 0;
        line-height: 12px;
    }

    > div:first-child {
        flex: 1;
    }

    > input {
        border: 0;
        height: fill-available;
        padding: 0 10px;
        font-size: 1em;

        :focus {
            border: 0;
            box-shadow: none;
        }
    }

    ${({$boxed, $hasError, theme}) =>
        $boxed &&
        css`
            && {
                height: 46px;
                padding: 0 14px;
                border: 1px solid
                    ${$hasError ? theme.colors.errorLabel?.main : '#c9d3e0'};
                box-shadow: none;
                color: #595959;
            }

            &&:focus-within {
                border-color: ${
                    $hasError
                        ? theme.colors.errorLabel?.main
                        : theme.colors.secondary?.main
                };
                box-shadow: 0 0 0 1px
                    ${
                        $hasError
                            ? theme.colors.errorLabel?.main
                            : theme.colors.secondary?.main
                    };
            }

            && > label {
                font-size: 0.9rem;
                line-height: normal;
            }

            && > label:first-child {
                flex: 1;
            }

            && > label:last-child {
                margin-left: 6px;
            }

            && > .form-control {
                flex: 0 1 90px;
                width: auto;
                min-width: 0;
                height: auto;
                padding: 0 5px 0 0;
                background: transparent;
                box-shadow: none;
                text-align: right;
                font-size: 1.05rem;
                font-weight: 700;
                color: #1f2937;
            }
        `}
`;
